import { useEffect, useState } from 'react';
import { StanInput, StanModal } from '@/components';
import { AffiliateReseller } from '../../types/affiliateAnalytics.types';
import { stanNotify } from '@/lib';
import { useAffiliateActions } from '../../queries/useAffiliateActions';

interface EditCommissionModalProps {
    pageId: string;
    isOpen: boolean;
    onClose: () => void;
    reseller: AffiliateReseller | null;
}

export function EditCommissionModal({ isOpen, pageId, onClose, reseller }: EditCommissionModalProps) {
    const [percentage, setPercentage] = useState<string>('');
    const { updateCommission, removeCommission } = useAffiliateActions(pageId);
    useEffect(() => {
        if (reseller?.affiliate_custom_percentage) {
            setPercentage(reseller.affiliate_custom_percentage.toString());
        }
    }, [reseller]);

    const handleUpdate = () => {
        if (!reseller) return;

        if (Number(percentage) < 10 || Number(percentage) > 90) {
            stanNotify({
                type: 'error',
                title: 'Update Affiliate Commission',
                text: 'Affiliate commission should be between 10 and 90',
                duration: 2000,
            });
            return;
        }

        updateCommission.mutate(
            { affiliate_custom_percentage: Number(percentage), affiliate_user_id: reseller.id },
            {
                onSuccess: () => {
                    stanNotify({
                        type: 'success',
                        title: 'Update Affiliate Commission',
                        text: 'Affiliate commission updated successfully',
                        duration: 2000,
                    });
                    handleClose();
                },
                onError: (error: any) => {
                    stanNotify({
                        type: 'error',
                        title: 'Unable to Update Affiliate Commission',
                        text: error.response?.data?.message || 'Failed to update commission',
                    });
                },
            }
        );
    };

    const handleRemove = () => {
        if (!reseller) return;

        removeCommission.mutate(reseller.id, {
            onSuccess: () => {
                stanNotify({
                    type: 'success',
                    title: 'Update Affiliate Commission',
                    text: 'Affiliate commission updated successfully',
                    duration: 2000,
                });
                handleClose();
            },
            onError: (error: any) => {
                stanNotify({
                    type: 'error',
                    title: 'Unable to Update Affiliate Commission',
                    text: error.response?.data?.message || 'Failed to update commission',
                });
            },
        });
    };

    const handleClose = () => {
        setPercentage('');
        onClose();
    };

    return (
        <StanModal
            id='edit-commission-modal'
            isOpen={isOpen}
            title='Edit Affiliate Commission'
            primaryActionLabel='Update Commission'
            secondaryActionLabel='Reset to Default Commission'
            onPrimaryAction={handleUpdate}
            onSecondaryAction={handleRemove}
            onClose={handleClose}
            primaryActionLoading={updateCommission.isPending || removeCommission.isPending}
            primaryActionDisabled={updateCommission.isPending || removeCommission.isPending}
        >
            <div className='flex items-center justify-center gap-2'>
                <StanInput
                    id='affiliate-percentage'
                    name='affiliate_percentage'
                    type='number'
                    value={percentage}
                    onChange={e => setPercentage(e.target.value)}
                    placeholder='ex. 60%'
                    className='w-24'
                />
                <div className='text-dark text-caption-1-regular'>
                    of future sales revenue will go to @<strong>{reseller?.username}</strong>
                </div>
            </div>
        </StanModal>
    );
}
